// Renders the customer's storefront account and its orders.

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { OrderCard } from './order-card';
import { StatTile } from './stat-tile';
import type { WorkspaceStorefrontDto } from './types';

const TABS = ['Orders', 'Drafts'] as const;

export function StorefrontCard({ storefront }: { storefront: WorkspaceStorefrontDto }) {
  // Open while there is something to show — data can stream in after mount — until the agent
  // toggles by hand, which then wins.
  const [manualOpen, setManualOpen] = useState<boolean | null>(null);
  const open = manualOpen ?? storefront.orders.length + storefront.drafts.length > 0;
  const [tab, setTab] = useState<(typeof TABS)[number]>('Orders');
  const [index, setIndex] = useState(0);

  const selectedTab = storefront.drafts.length > 0 ? tab : 'Orders';
  const orders = selectedTab === 'Orders' ? storefront.orders : storefront.drafts;
  const currentIndex = Math.min(index, Math.max(orders.length - 1, 0));
  const current = orders[currentIndex];

  return (
    <div
      className="flex w-full flex-col border-l-4 border-solid bg-gray-100"
      style={{ borderColor: storefront.accentColor }}
    >
      <div className="flex w-full flex-col bg-white px-5 pb-4 pt-4">
        <CollapseHeader open={open} label={storefront.provider} variant="chevron" onToggle={() => setManualOpen(!open)} className="h-5">
          <div className="flex min-w-0 items-center gap-2">
            <img src={storefront.logo} alt={storefront.provider} className="size-5 shrink-0 object-contain" />
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">{storefront.provider}</p>
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        {/* The header's 16px padding plus this 6px keep the 22px gap the open card had. */}
        <div className="flex w-full flex-col gap-3 bg-white px-5 pb-5 pt-1.5">
          <div className="flex w-full items-start gap-2">
            <StatTile value={String(storefront.totalOrders)} label="Total orders" />
            <StatTile value={storefront.totalSpent} label="Total spent" />
          </div>
          {storefront.customerSince && (
            <div className="flex w-full items-center justify-between gap-3 text-[12px] leading-[1.1]">
              <p className="text-gray-700">Customer since</p>
              <p className="font-semibold text-text-prose">{storefront.customerSince}</p>
            </div>
          )}
          {storefront.tags.length > 0 && (
            <div className="flex w-full items-center justify-between gap-3">
              <p className="text-[12px] leading-[1.1] text-gray-700">Tags</p>
              <div className="flex flex-wrap items-center justify-end gap-1">
                {storefront.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-xl bg-indigo-100 px-2 py-1 text-[12px] leading-[1.1] text-text-prose"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-3 px-4 py-3">
          <div className="flex w-full items-center justify-between gap-2">
            {/* With no drafts the toggle has nowhere to go, so it becomes a plain heading. */}
            {storefront.drafts.length === 0 ? (
              <p className="text-[12px] font-semibold leading-[1.1] text-text-body">Orders</p>
            ) : (
              <div className="relative flex w-[124px] items-center rounded-full border border-gray-400 bg-gray-200 p-0.5">
                {/* One pill that slides, so the selection travels instead of jumping. */}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full border border-gray-200 bg-white shadow-[0_0_4px_rgba(0,0,0,0.1)] transition-transform duration-200 ease-out motion-reduce:transition-none',
                    selectedTab === 'Drafts' && 'translate-x-full',
                  )}
                />
                {TABS.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => {
                      setTab(name);
                      setIndex(0);
                    }}
                    className={cn(
                      'relative z-10 flex-1 cursor-pointer rounded-full py-1.5 text-[12px] leading-[1.1] transition-colors duration-200 motion-reduce:transition-none',
                      selectedTab === name ? 'font-semibold text-text-body' : 'text-gray-900',
                    )}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
            {orders.length > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                  className="text-gray-900"
                  aria-label="Previous order"
                >
                  <ChevronLeft className="size-[14px]" />
                </Button>
                <p className="text-[12px] font-semibold leading-[1.1] text-text-body">
                  {currentIndex + 1}/{orders.length}
                </p>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIndex((prev) => Math.min(orders.length - 1, prev + 1))}
                  disabled={currentIndex >= orders.length - 1}
                  className="text-gray-900"
                  aria-label="Next order"
                >
                  <ChevronRight className="size-[14px]" />
                </Button>
              </div>
            )}
          </div>

          {current ? (
            <OrderCard order={current} />
          ) : (
            <p className="py-4 text-center text-[12px] leading-[1.1] text-gray-700">No {selectedTab.toLowerCase()} to show</p>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
